import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaTrashAlt, FaTimes } from "react-icons/fa";
import "./InvoiceModal.css";

const InvoiceModal = ({
  consultation,
  onClose,
  refreshConsultations,
  patientId,
  billType = "treatment",
}) => {
  const [doctorFees, setDoctorFees] = useState("");
  const [discount, setDiscount] = useState(0);
  const [showDiscountInput, setShowDiscountInput] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const authData = JSON.parse(localStorage.getItem("authData"));
  const token = authData?.accessToken;

  const isIPD = consultation?.isIPD || false;
  const isMedicineBill = billType === "medicine";

  const normalizeTreatment = (t) => {
    if (typeof t === "string") return { name: t, cost: 0 };
    return { name: t.name, cost: t.cost ?? 0 };
  };

  // Visits-aware: treatments and medicines live on visits[], not the parent consultation.
  const rawTreatments = isIPD
    ? consultation.treatments || []
    : consultation.visits?.length > 0
      ? consultation.visits.flatMap((v) => v.treatments || [])
      : consultation.treatments || [];

  const treatments = rawTreatments.map(normalizeTreatment);

  const rawMedicines = isIPD
    ? consultation.medicines || []
    : consultation.visits?.length > 0
      ? consultation.visits.flatMap((v) => v.prescribedMedicines || [])
      : consultation.prescribedMedicines || [];

  // Normalize medicine structure
  const medicines = rawMedicines.map((m) => ({
    id: m.medicineId || m._id || m.id,
    name: m.name,
    dosage: m.dosage,
    price: m.price ?? m.cost ?? 0,
  }));

  const [roomRate, setRoomRate] = useState("");
  const [nursingRate, setNursingRate] = useState("");
  const [serviceCharges, setServiceCharges] = useState([]);
  const [currentService, setCurrentService] = useState({ name: "", cost: "" });

  const handleGenerateInvoice = async () => {
    if (!isMedicineBill && (!doctorFees || isNaN(doctorFees))) {
      toast.error("Please enter valid doctor fees.");
      return;
    }

    if (isMedicineBill && medicines.length === 0) {
      toast.error("No medicines to bill.");
      return;
    }

    const appointmentId = consultation.appointment?._id || consultation.appointment;
    const medicineLines = medicines.map((m) => ({
      medicineId: m.id || m._id,
      name: m.name,
      cost: Number(m.price) || 0,
      quantity: parseFloat(m.dosage) || 1,
    }));

    let payload;
    let endpoint;

    if (isIPD) {
      payload = {
        admissionId: consultation._id,
        invoiceType: billType,
      };

      if (isMedicineBill) {
        payload.medicines = medicineLines;
      } else {
        payload.doctorFee = Number(doctorFees);
        if (roomRate) {
          payload.roomCharges = { perDayRate: Number(roomRate) };
        }
        if (nursingRate) {
          payload.nursingCharges = { perDayRate: Number(nursingRate) };
        }
        if (serviceCharges.length > 0) {
          payload.services = serviceCharges.map((sc) => ({
            name: sc.name,
            cost: Number(sc.cost),
          }));
        }
      }

      endpoint = "generate-ipd-invoice";
    } else if (isMedicineBill) {
      payload = {
        patientId,
        appointmentId,
        invoiceType: "medicine",
        doctorFee: 0,
        treatments: [],
        medicines: medicineLines,
      };
      endpoint = "generate-invoice";
    } else {
      payload = {
        patientId,
        appointmentId,
        invoiceType: "treatment",
        doctorFee: Number(doctorFees),
        treatments: treatments.map((t) => ({
          name: typeof t === "string" ? t : t.name,
          cost: typeof t === "string" ? 0 : Number(t.cost) || 0,
        })),
        medicines: [],
      };
      endpoint = "generate-invoice";
    }

    try {
      setLoading(true);
      await axios.post(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Invoice generated successfully!");

      // Refresh consultations in parent
      if (refreshConsultations) {
        refreshConsultations();
      }

      onClose();
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || "Failed to generate invoice. Try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="invoice-modal-overlay">
      <div className="invoice-modal-content">
        <div className="invoice-modal-header">
          <h3>{isMedicineBill ? "Create Medicine Invoice" : "Create Treatment Invoice"}</h3>
          <button
            type="button"
            className="invoice-modal-close-btn"
            onClick={onClose}
            disabled={loading}
          >
            <FaTimes />
          </button>
        </div>

        <div className="invoice-modal-body">
          {!isMedicineBill && (
          <div className="invoice-modal-section">
            <h4>Treatments</h4>
            <ul>
              {treatments.map((t, i) => (
                <li key={i}>
                  {typeof t === "string" ? t : t.name} - ₹{typeof t === "string" ? 0 : t.cost}
                </li>
              ))}
            </ul>
          </div>
          )}

          {isMedicineBill && (
          <div className="invoice-modal-section">
            <h4>Medicines</h4>
            <ul>
              {medicines.map((m, i) => (
                <li key={i}>
                  {m.name} - ₹{m.price} × {m.dosage || 1} = ₹
                  {(m.price * (parseFloat(m.dosage) || 1)).toFixed(2)}
                </li>
              ))}
            </ul>
          </div>
          )}

          {isIPD && !isMedicineBill && (
            <>
              <div className="invoice-modal-form-group">
                <label>Room Charges (Per Day Rate)</label>
                <input
                  type="number"
                  placeholder="Enter Rate"
                  value={roomRate}
                  onChange={(e) => setRoomRate(e.target.value)}
                />
              </div>

              <div className="invoice-modal-form-group">
                <label>Nursing Charges (Per Day Rate)</label>
                <input
                  type="number"
                  placeholder="Enter Rate"
                  value={nursingRate}
                  onChange={(e) => setNursingRate(e.target.value)}
                />
              </div>

              <div className="invoice-modal-section">
                <h4>Service Charges</h4>
                <div className="invoice-modal-service-inputs">
                  <input
                    type="text"
                    className="invoice-modal-service-name-input"
                    placeholder="Service Name"
                    value={currentService.name}
                    onChange={(e) =>
                      setCurrentService({ ...currentService, name: e.target.value })
                    }
                  />
                  <input
                    type="number"
                    className="invoice-modal-service-cost-input"
                    placeholder="Cost"
                    value={currentService.cost}
                    onChange={(e) =>
                      setCurrentService({ ...currentService, cost: e.target.value })
                    }
                  />
                  <button
                    type="button"
                    className="invoice-modal-add-service-btn"
                    onClick={() => {
                      if (currentService.name && currentService.cost) {
                        setServiceCharges([...serviceCharges, currentService]);
                        setCurrentService({ name: "", cost: "" });
                      } else {
                        toast.error("Please enter both service name and cost.");
                      }
                    }}
                  >
                    Add Service
                  </button>
                </div>
                {serviceCharges.length > 0 && (
                  <div className="invoice-modal-service-list">
                    <ul className="invoice-modal-service-list-items">
                      {serviceCharges.map((sc, i) => (
                        <li key={i} className="invoice-modal-service-item">
                          <div className="invoice-modal-service-item-content">
                            <span className="invoice-modal-service-name">{sc.name}</span>
                            <span className="invoice-modal-service-cost">₹{sc.cost}</span>
                          </div>
                          <button
                            type="button"
                            className="invoice-modal-remove-service-btn"
                            onClick={() => {
                              setServiceCharges(serviceCharges.filter((_, index) => index !== i));
                            }}
                            title="Remove service"
                          >
                            <FaTrashAlt />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </>
          )}

          {!isMedicineBill && (
          <div className="invoice-modal-form-group">
            <label>Doctor Fees (₹)</label>
            <input
              type="number"
              value={doctorFees}
              onChange={(e) => setDoctorFees(e.target.value)}
              required
            />
          </div>
          )}
        </div>

        <div className="invoice-modal-footer">
          <button
            type="button"
            className="invoice-modal-cancel-btn"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="invoice-modal-submit-btn"
            type="button"
            onClick={handleGenerateInvoice}
            disabled={loading}
          >
            {loading ? "Generating..." : "Generate Invoice"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;
